/**
 * Leitura única de erros da API no frontend.
 *
 * O backend responde com `{ error, code, message, mensagem }` — `error` é o
 * campo canônico e os outros dois existem como alias temporários (ver
 * `backend/middlewares/errorHandler.js` e docs/ROADMAP.md, item 1). Em vez de
 * cada tela escolher um campo, use `mensagemDeErroApi`:
 *
 *   catch (erro) {
 *     mostrarAlerta(mensagemDeErroApi(erro, 'Não foi possível salvar o pedido.'));
 *   }
 */

const MENSAGEM_PADRAO = 'Algo deu errado. Tente novamente.';

/** Normaliza o candidato para string legível (aceita string ou `{ message }`). */
const comoTexto = (valor) => {
  if (typeof valor === 'string' && valor.trim()) return valor.trim();

  if (valor && typeof valor === 'object') {
    const mensagem = valor.message || valor.mensagem;
    if (typeof mensagem === 'string' && mensagem.trim()) return mensagem.trim();
  }

  return null;
};

/**
 * Mensagem pronta para exibir ao usuário.
 *
 * @param {unknown} erro erro do axios (ou qualquer objeto com `response.data`)
 * @param {string} padrao mensagem usada quando nada aproveitável é encontrado
 */
const mensagemDeErroApi = (erro, padrao = MENSAGEM_PADRAO) => {
  if (!erro) return padrao;

  // Sem resposta do servidor: rede, CORS, servidor fora do ar ou timeout.
  if (!erro.response) {
    if (erro.code === 'ECONNABORTED' || erro.code === 'ETIMEDOUT') {
      return 'A requisição demorou demais. Tente novamente.';
    }

    if (erro.code === 'ERR_CANCELED') return padrao;

    return 'Não foi possível conectar ao servidor. Verifique sua conexão.';
  }

  const dados = erro.response.data;
  const candidatos = [dados?.error, dados?.mensagem, dados?.message, dados?.erro, erro.message];

  for (const candidato of candidatos) {
    const texto = comoTexto(candidato);
    if (texto) return texto;
  }

  return padrao;
};

/** Código estável do erro (`VALIDATION_ERROR`, `PAYMENT_REQUIRED`, ...). */
const codigoDeErroApi = (erro) => erro?.response?.data?.code || null;

/** Status HTTP da resposta (ou `null` quando não houve resposta). */
const statusDeErroApi = (erro) => erro?.response?.status ?? null;

/** Detalhes estruturados (ex.: campos inválidos) quando existirem. */
const detalhesDeErroApi = (erro) => erro?.response?.data?.details ?? null;

export { MENSAGEM_PADRAO, codigoDeErroApi, detalhesDeErroApi, mensagemDeErroApi, statusDeErroApi };
